import { LoadingPage } from "@qubit/autoparts";
import { ReactNode, useEffect } from "react";

import { Outlet, useLocation, Navigate } from "react-router-dom";

import useModeUpdate from "~/hooks/useModeUpdate";
import {
  selectAuthHeaderReady,
  selectClientConfigReady,
  selectFlagsReadyClientId
} from "~/redux/selectors/appSelectors";

import { selectIsUserLoggedIn } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import { setSignalREnabled } from "./app.slice";
import { useAppDispatch, useAppSelector } from "./store";

type PrivateRouteProps = {
  children?: ReactNode;
};

export function PrivateRoute({ children }: PrivateRouteProps): JSX.Element {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const normalizedPath = location.pathname.toLowerCase();
  const isSettingsPath = normalizedPath === "/settings";

  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const loginPath =
    useAppSelector((state) => state.app.siteLoginPath) || "/login";

  const isAuthHeaderReady = useAppSelector(selectAuthHeaderReady);
  const clientConfigReady = useAppSelector(selectClientConfigReady);
  const isFlagsReady = useAppSelector(selectFlagsReadyClientId);

  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);

  useModeUpdate({ pathname: location.pathname });

  // enable signalR if the user is logged in and has a fulfillment center
  const signalREnabled = useAppSelector((state) => state.app.signalREnabled);

  useEffect(() => {
    if (
      isUserLoggedIn &&
      usersFulfillmentCenter?.fulfillmentCenterId &&
      !signalREnabled
    ) {
      dispatch(setSignalREnabled(true));
    }
  }, [isUserLoggedIn, usersFulfillmentCenter, signalREnabled, dispatch]);

  if (!isUserLoggedIn) {
    return <Navigate to={loginPath} replace />;
  }

  // Wait until configuration and flags are ready.
  if (!isAuthHeaderReady || !clientConfigReady || !isFlagsReady) {
    return <LoadingPage loadingMessage="Loading configuration..." />;
  }

  // If the user doesn't have a fulfillment center and is not on settings, redirect.
  if (!usersFulfillmentCenter && !isSettingsPath) {
    return <Navigate to={"/settings"} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
